/**
 * Web Electron Shim
 * Replaces window.electron IPC calls with HTTP fetch calls for the web app.
 * Imported from src/index.js before any renderer module reads window.electron.
 */
import { resolveAppleArtworkOnly, appleArtworkOnlyNow, stripNonAppleArtwork } from './shared/appleArtwork';

(function () {
  // Skip if real Electron is present (preload.js sets window.electron.db
  // to an IPC object) OR if capacitor-shim already populated the native
  // runtime (marked with __native: true). The two together cover every
  // case where we shouldn't overwrite an existing window.electron.
  if (window.electron && (window.electron.db || window.electron.__native)) return;
  // Apple-only artwork (src/shared/appleArtwork.js): off on web by default; REACT_APP_APPLE_ARTWORK_ONLY=1 turns it on.
  // The live server mode (utils/appleArtworkMode.js polls it) wins once known.
  const APPLE_ARTWORK_BUILD = resolveAppleArtworkOnly(process.env.REACT_APP_APPLE_ARTWORK_ONLY, false);
  const appleArtworkOnly = () => appleArtworkOnlyNow(false, APPLE_ARTWORK_BUILD);

  // ── Helpers ──

  // credentials:'include' lets the browser send mg_access / mg_refresh / mg_user
  // cookies on cross-origin requests to auth.mixgoat.com. Bearer headers stay
  // for backward compat — auth service accepts either.
  //
  // Local dev fallback: HttpOnly cookies set by the deployed auth service
  // don't reliably persist on localhost (cross-origin storage rules, the
  // CRA dev proxy, etc.), so we additionally cache the access token from
  // any auth response and inject it as Bearer when the caller didn't pass
  // one. Production behavior is unchanged because cookies work natively
  // there; the cached token is just a redundant safety net.
  const IS_LOCAL_DEV = typeof window !== 'undefined'
    && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  // localStorage-backed so page refresh doesn't log the dev out. Only
  // active on localhost — production never reads/writes these keys.
  const LS_ACCESS = 'web-shim:dev-access-token';
  const LS_REFRESH = 'web-shim:dev-refresh-token';
  const LS_USER = 'web-shim:dev-user';
  let cachedAccessToken = IS_LOCAL_DEV ? (localStorage.getItem(LS_ACCESS) || null) : null;
  let cachedRefreshToken = IS_LOCAL_DEV ? (localStorage.getItem(LS_REFRESH) || null) : null;

  function rememberTokenFromResponse(data) {
    if (!IS_LOCAL_DEV || !data) return;
    if (data.accessToken) {
      cachedAccessToken = data.accessToken;
      try { localStorage.setItem(LS_ACCESS, data.accessToken); } catch (_) { /* quota / private mode */ }
    }
    if (data.refreshToken) {
      cachedRefreshToken = data.refreshToken;
      try { localStorage.setItem(LS_REFRESH, data.refreshToken); } catch (_) { /* quota */ }
    }
    // Cache the user object so checkAuthStatus on a fresh reload can
    // restore the session without needing a working /refresh or /me
    // call (cookie-dependent paths are unreliable on localhost).
    if (data.user && typeof data.user === 'object') {
      try { localStorage.setItem(LS_USER, JSON.stringify(data.user)); } catch (_) {}
    }
  }

  function clearCachedTokens() {
    cachedAccessToken = null;
    cachedRefreshToken = null;
    try { localStorage.removeItem(LS_ACCESS); } catch (_) {}
    try { localStorage.removeItem(LS_REFRESH); } catch (_) {}
    try { localStorage.removeItem(LS_USER); } catch (_) {}
  }

  function authHeaderToken(explicitToken) {
    if (explicitToken) return explicitToken;
    if (IS_LOCAL_DEV && cachedAccessToken) return cachedAccessToken;
    return null;
  }

  function refreshTokenForBody(explicitToken) {
    if (explicitToken) return explicitToken;
    if (IS_LOCAL_DEV && cachedRefreshToken) return cachedRefreshToken;
    return undefined;
  }

  // Single-flight guard for /api/auth/refresh. The auth service ROTATES the
  // refresh token for cookie-based (web) clients: the first refresh revokes
  // the mg_refresh cookie's token and issues a new one. When the 15-min
  // access token expires, many thunks 401 at once and each calls
  // auth.refreshToken() — without this guard the racing calls all send the
  // now-revoked cookie, get 401 "Invalid refresh token", and force a logout
  // (the "logged out every few minutes" bug). Sharing one in-flight refresh
  // promise makes the whole burst ride a single rotation.
  let refreshInFlight = null;
  // This browser's identity for the auth service's device list: a random id
  // minted once per browser profile (survives sign-out on purpose) and a
  // readable label. The cookie session rotates server-side already; the
  // device is what lets the account see "Chrome on macOS" and sign it out.
  const DEVICE_ID_KEY = 'mg:device-id';
  function deviceInfo() {
    let id = null;
    try { id = localStorage.getItem(DEVICE_ID_KEY); } catch (_) { /* private mode */ }
    if (!id) {
      id = (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : `web-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      try { localStorage.setItem(DEVICE_ID_KEY, id); } catch (_) { /* keep in memory */ }
    }
    const ua = navigator.userAgent || '';
    const browser = /Edg\//.test(ua) ? 'Edge' : /OPR\//.test(ua) ? 'Opera' : /Firefox\//.test(ua) ? 'Firefox' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : 'Browser';
    const os = /Windows/.test(ua) ? 'Windows' : /Mac OS X|Macintosh/.test(ua) ? 'macOS' : /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS' : /Linux/.test(ua) ? 'Linux' : '';
    return { id, name: os ? `${browser} on ${os}` : browser, platform: 'web' };
  }

  function singleFlightRefresh(refreshToken) {
    if (refreshInFlight) return refreshInFlight;
    refreshInFlight = apiPost('/api/auth/refresh', { refreshToken: refreshTokenForBody(refreshToken) })
      .finally(() => { refreshInFlight = null; });
    return refreshInFlight;
  }

  async function apiPost(url, body, token) {
    const headers = { 'Content-Type': 'application/json' };
    const t = authHeaderToken(token);
    if (t) headers['Authorization'] = `Bearer ${t}`;
    const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body), credentials: 'include' });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      // Keep the server's machine-readable fields: the sign-in UI tells an
      // account lock, a captcha gate and a wrong code apart by `code`.
      return {
        error: err.error || err.message || `Request failed (${res.status})`,
        status: res.status,
        ...(err.code ? { code: err.code } : {}),
        ...(err.retryAfterSeconds ? { retryAfterSeconds: err.retryAfterSeconds } : {}),
        ...(err.attemptsLeft !== undefined ? { attemptsLeft: err.attemptsLeft } : {}),
        ...(err.siteKey ? { siteKey: err.siteKey } : {}),
      };
    }
    const data = await res.json();
    rememberTokenFromResponse(data);
    if (appleArtworkOnly()) { try { stripNonAppleArtwork(data); } catch (_) { /* never break a response over cover filtering */ } }
    return data;
  }

  async function apiGet(url, token) {
    const headers = {};
    const t = authHeaderToken(token);
    if (t) headers['Authorization'] = `Bearer ${t}`;
    const res = await fetch(url, { headers, credentials: 'include' });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      return { error: err.error || err.message || `Request failed (${res.status})`, status: res.status };
    }
    const data = await res.json();
    rememberTokenFromResponse(data);
    if (appleArtworkOnly()) { try { stripNonAppleArtwork(data); } catch (_) { /* never break a response over cover filtering */ } }
    return data;
  }

  async function apiPut(url, body, token) {
    const headers = { 'Content-Type': 'application/json' };
    const t = authHeaderToken(token);
    if (t) headers['Authorization'] = `Bearer ${t}`;
    const res = await fetch(url, { method: 'PUT', headers, body: JSON.stringify(body), credentials: 'include' });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      return { error: err.error || err.message || `Request failed (${res.status})`, status: res.status };
    }
    return res.json();
  }

  async function apiPatch(url, body, token) {
    const headers = { 'Content-Type': 'application/json' };
    const t = authHeaderToken(token);
    if (t) headers['Authorization'] = `Bearer ${t}`;
    const res = await fetch(url, { method: 'PATCH', headers, body: JSON.stringify(body), credentials: 'include' });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      return { error: err.error || err.message || `Request failed (${res.status})`, status: res.status };
    }
    return res.json();
  }

  async function apiDelete(url, token, body) {
    const headers = {};
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    const t = authHeaderToken(token);
    if (t) headers['Authorization'] = `Bearer ${t}`;
    const res = await fetch(url, {
      method: 'DELETE',
      headers,
      credentials: 'include',
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
    if (res.status === 204) return { success: true };
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      return { error: err.error || err.message || `Request failed (${res.status})`, status: res.status };
    }
    return res.json();
  }

  // ── Tier (server-side, mirrors tierService.js) ──

  function getTrialInfo(user) {
    const trialStartStr = user?.trial_started_at;
    const trialEndStr = user?.trial_ends_at;
    if (!trialStartStr || !trialEndStr) return { trialStartDate: null, trialEndDate: null, daysRemaining: 0, isTrialActive: false, isTrialExpired: false };
    const trialStart = new Date(trialStartStr);
    const trialEnd = new Date(trialEndStr);
    const msRemaining = trialEnd.getTime() - Date.now();
    const daysRemaining = Math.max(0, Math.ceil(msRemaining / (1000 * 60 * 60 * 24)));
    return { trialStartDate: trialStart.toISOString(), trialEndDate: trialEnd.toISOString(), daysRemaining, isTrialActive: msRemaining > 0, isTrialExpired: msRemaining <= 0 };
  }

  // ── Shim ──

  window.electron = {
    // ── Auth ──
    auth: {
      // The renderer shows the email-code step when the server answers 202.
      login: (email, password) => apiPost('/api/auth/login', { email, password, device: deviceInfo(), supportsChallenge: true }),
      loginVerify: (challengeId, code) => apiPost('/api/auth/login/verify', { challengeId, code, device: deviceInfo() }),
      loginResend: (challengeId) => apiPost('/api/auth/login/resend', { challengeId }),
      listSessions: (accessToken) => apiGet('/api/auth/sessions', accessToken),
      revokeSession: (accessToken, sessionId) => apiPost(`/api/auth/sessions/${encodeURIComponent(sessionId)}/revoke`, {}, accessToken),
      revokeOtherSessions: (accessToken) => apiPost('/api/auth/sessions/revoke-others', {}, accessToken),
      register: (data) => apiPost('/api/auth/register', data),
      logout: (refreshToken) => {
        const r = apiPost('/api/auth/logout', { refreshToken: refreshTokenForBody(refreshToken) });
        clearCachedTokens();
        return r;
      },
      refreshToken: (refreshToken) => singleFlightRefresh(refreshToken),
      getProfile: (accessToken) => apiGet('/api/auth/me', accessToken),
      // Parity with capacitor-shim. The delete-account UI is gated to native
      // mobile, so web/desktop never call this — but the method must exist for
      // shim-parity and so the website can adopt deletion later.
      deleteAccount: (accessToken, payload) => apiPost('/api/auth/delete-account', payload || {}, accessToken),
      verifyEmail: (token) => apiGet(`/api/auth/verify-email?token=${encodeURIComponent(token)}`),
      forgotPassword: (email) => apiPost('/api/auth/forgot-password', { email }),
      resetPassword: (data) => apiPost('/api/auth/reset-password', data),
      googleLogin: () => {
        // Navigate directly to auth service (not through proxy) so Google sees the browser's cookies
        // and shows the account picker instead of the email entry screen
        const callbackUrl = window.location.origin + '/auth/google/callback';
        window.location.href = 'https://auth.mixgoat.com/api/auth/google?web_redirect=' + encodeURIComponent(callbackUrl);
        // This promise never resolves — the page navigates away
        return new Promise(() => {});
      },
      cancelGoogleLogin: () => Promise.resolve(),
      // Apple Sign in (web flow) — same redirect pattern as Google. The web
      // app at mixgoat.com hosts /auth/apple/callback to consume the ticket.
      appleLogin: () => {
        const callbackUrl = window.location.origin + '/auth/apple/callback';
        window.location.href = 'https://auth.mixgoat.com/api/auth/apple/web?web_redirect=' + encodeURIComponent(callbackUrl);
        return new Promise(() => {});
      },
      cancelAppleLogin: () => Promise.resolve(),
      manageSubscription: (accessToken) => apiGet('/api/subscriptions/current', accessToken),
    },

    // ── Library (likes, follows, notifications) ──
    library: {
      // Track likes
      getLikes: (token, page, limit) => apiGet(`/api/library/likes?page=${page}&limit=${limit}&type=track`, token),
      likeTrack: (token, track) => apiPost('/api/library/likes', track, token),
      unlikeTrack: (token, artist, title) => apiPost('/api/library/likes/unlike', { artist, title }, token),
      checkLikes: (token, tracks) => apiPost('/api/library/likes/check', { tracks, withNotify: true }, token),
      setTrackNotify: (token, artist, title, notify) => apiPost('/api/library/likes/notify', { artist, title, notify }, token),
      // Mix likes
      getLikedMixes: (token, page, limit) => apiGet(`/api/library/likes?page=${page}&limit=${limit}&type=mix`, token),
      likeMix: (token, mix) => apiPost('/api/library/likes', { ...mix, type: 'mix' }, token),
      unlikeMix: (token, title, djName) => apiPost('/api/library/likes/unlike', { artist: djName, title, type: 'mix' }, token),
      checkMixLikes: (token, mixes) => apiPost('/api/library/likes/check', { tracks: mixes, type: 'mix' }, token),
      // Artist follows
      getFollowedArtists: (token, page, limit) => apiGet(`/api/library/artists?page=${page}&limit=${limit}`, token),
      followArtist: (token, artist) => apiPost('/api/library/artists', artist, token),
      unfollowArtist: (token, artistName) => apiPost('/api/library/artists/unfollow', { artistName }, token),
      checkFollowedArtists: (token, artistNames) => apiPost('/api/library/artists/check', { artistNames, withNotify: true }, token),
      setArtistNotify: (token, artistName, notify) => apiPost('/api/library/artists/notify', { artistName, notify }, token),
      // Label follows
      getFollowedLabels: (token, page, limit) => apiGet(`/api/library/labels?page=${page}&limit=${limit}`, token),
      followLabel: (token, label) => apiPost('/api/library/labels', label, token),
      unfollowLabel: (token, labelPublicId) => apiPost('/api/library/labels/unfollow', { labelPublicId }, token),
      checkFollowedLabels: (token, labelPublicIds) => apiPost('/api/library/labels/check', { labelPublicIds }, token),
      setLabelNotify: (token, labelPublicId, notify) => apiPost('/api/library/labels/notify', { labelPublicId, notify }, token),
      // Notifications
      getNotifications: (token, page, limit) => apiGet(`/api/library/notifications?page=${page}&limit=${limit}`, token),
      getNotificationUnreadCount: (token) => apiGet('/api/library/notifications/unread-count', token),
      getNotificationSettings: (token) => apiGet('/api/library/notifications/settings', token),
      updateNotificationSettings: (token, settings) => apiPut('/api/library/notifications/settings', settings, token),
      // Preferences (theme, etc.). PATCH so partial updates are explicit and
      // we don't accidentally clobber unrelated fields the server may add.
      getPreferences: (token) => apiGet('/api/library/preferences', token),
      updatePreferences: (token, prefs) => apiPatch('/api/library/preferences', prefs, token),
      markNotificationRead: (token, notificationId) => apiPost('/api/library/notifications/read', { notificationId }, token),
      markAllNotificationsRead: (token) => apiPost('/api/library/notifications/read-all', {}, token),
      deleteNotification: (token, notificationId) => apiPost('/api/library/notifications/delete', { notificationId }, token),
      deleteAllNotifications: (token) => apiPost('/api/library/notifications/delete-all', {}, token),
      // Server-side DJ library (synced up by the desktop app) — mobile/web's
      // source for "Artists from your library" (no local SQLite here).
      getDJLibraryArtists: (token, limit) => apiGet(`/api/library/dj-library/artists?limit=${limit || 200}`, token),
      // Playlists (server-side, mirrors db.playlists interface for web)
      getPlaylists: (token) => apiGet('/api/library/playlists', token),
      searchPlaylistsByTrack: (token, q) => apiGet('/api/library/playlists/search-by-track?q=' + encodeURIComponent(q), token),
      getPlaylist: (token, id) => apiGet(`/api/library/playlists/${id}`, token),
      createPlaylist: (token, data) => apiPost('/api/library/playlists', data, token),
      updatePlaylist: (token, id, data) => apiPut(`/api/library/playlists/${id}`, data, token),
      deletePlaylist: (token, id) => apiDelete(`/api/library/playlists/${id}`, token),
      addTrackToPlaylist: (token, playlistId, track) => apiPost(`/api/library/playlists/${playlistId}/tracks`, track, token),
      // rowId = playlist-track ROW id, not the catalog track_id (desktop-only
      // backfill path in practice; defined for API parity)
      setPlaylistTrackFilePath: (token, playlistId, rowId, filePath) => apiPatch(`/api/library/playlists/${playlistId}/tracks/${rowId}/file-path`, { file_path: filePath }, token),
      removeTrackFromPlaylist: (token, playlistId, trackId) => apiDelete(`/api/library/playlists/${playlistId}/tracks/${trackId}`, token),
      // By ROW PK — the only removal path for local-file/mix rows (track_id NULL).
      removePlaylistRow: (token, playlistId, rowId) => apiDelete(`/api/library/playlists/${playlistId}/tracks/rows/${rowId}`, token),
      // rowIds = user_playlist_tracks.id (the `id` on each row the playlist GET returns)
      reorderPlaylistTracks: (token, playlistId, rowIds) => apiPut(`/api/library/playlists/${playlistId}/tracks/reorder`, { rowIds }, token),
      getPlannerSets: (token) => apiGet('/api/library/planner-sets', token),
      savePlannerSet: (token, setId, data) => apiPut(`/api/library/planner-sets/${setId}`, data, token),
      deletePlannerSet: (token, setId) => apiDelete(`/api/library/planner-sets/${setId}`, token),
      // Search history. `surface` filters by feature area (e.g. 'mixpedia',
      // 'planner') and must be forwarded so each surface gets its own recents.
      getSearchHistory: (token, limit, surface) => {
        const params = new URLSearchParams({ limit: String(limit || 10) });
        if (surface) params.set('surface', surface);
        return apiGet(`/api/library/search-history?${params.toString()}`, token);
      },
      addSearchHistory: (token, entry) => apiPost('/api/library/search-history', entry, token),
      deleteSearchHistoryItem: (token, id) => apiDelete(`/api/library/search-history/${id}`, token),
      clearSearchHistory: (token, surface) => {
        const qs = surface ? `?surface=${encodeURIComponent(surface)}` : '';
        return apiDelete(`/api/library/search-history${qs}`, token);
      },
    },

    // ── DJPal (Mixpedia, recommendations) ──
    djpal: {
      getToken: (accessToken) => apiPost('/api/auth/djpal-token', {}, accessToken),
      validateToken: (token) => apiPost('/api/djpal/auth/token/validate', { token }),
      searchMixes: (token, filters, dateRange, pagination) => apiPost('/api/djpal/mixes/search', { filters, dateRange, ...pagination }, token),
      searchMixesLite: (token, filters, dateRange, pagination) => apiPost('/api/djpal/mixes/search-lite', { filters, dateRange, ...pagination }, token),
      // Mixpedia home rows (mirrors the Electron IPC handlers)
      latestMixesByArtists: (token, artists, page, limit, artistIds) => apiPost('/api/djpal/mixes/latest-by-artists', { artists, page, limit, ...(Array.isArray(artistIds) && artistIds.length ? { artistIds } : {}) }, token).then(r => ({ results: r?.data?.results || r?.results || [], meta: r?.meta || null })).catch(e => ({ error: e.message })),
      latestTracksByArtists: (token, artists, page, limit, seed, artistIds) => apiPost('/api/djpal/mixes/latest-tracks-by-artists', { artists, page, limit, seed, ...(Array.isArray(artistIds) && artistIds.length ? { artistIds } : {}) }, token).then(r => ({ results: r?.data?.results || r?.results || [], meta: r?.meta || null })).catch(e => ({ error: e.message })),
      tracksByArtistsRecentlyPlayed: (token, artists, page, limit, artistIds) => apiPost('/api/djpal/tracks/by-artists-recently-played', { artists, page, limit, ...(Array.isArray(artistIds) && artistIds.length ? { artistIds } : {}) }, token).then(r => ({ results: r?.data?.results || r?.results || [], meta: r?.meta || null })).catch(e => ({ error: e.message })),
      resolveMixesByLinks: (token, links) => apiPost('/api/djpal/mixes/resolve-by-links', { links }, token),
      // Batch catalog lookup by public id (likes re-hydration) — mirrors the
      // Electron IPC's bare-array return (api-client's `json.data ?? json`).
      tracksByPublicIds: (token, publicIds) =>
        apiPost('/api/djpal/tracks/by-public-ids', { publicIds }, token)
          .then(r => (r && r.error) ? r : (r?.data ?? r))
          .catch(e => ({ error: e.message })),
      searchArtistTracks: (token, params) => apiPost('/api/djpal/artists/tracks-search', params, token),
      searchTrackMixAppearances: (token, params) => apiPost('/api/djpal/tracks/mix-appearances', params, token),
      getPopularArtists: (token) => apiGet('/api/djpal/artists/popular?limit=100', token),
      // Which of these raw artist names does Mixpedia know? (library follow
      // suggestions) — mirrors the Electron IPC 'djpal:feedResolveArtistsV2'.
      feedResolveArtistsV2: (token, artists) => apiPost('/api/djpal/feed/v2/resolve-artists', { artists }, token),
      autocompleteArtist: (token, q) => apiGet(`/api/djpal/artists/autocomplete?q=${encodeURIComponent(q)}`, token),
      autocompleteLabels: (token, q) => apiGet(`/api/djpal/labels/autocomplete?q=${encodeURIComponent(q)}&limit=8`, token),
      getLabel: (token, publicId, opts = {}) => apiGet(`/api/djpal/labels/${encodeURIComponent(publicId)}?page=${opts.page || 1}&limit=${opts.limit || 50}${opts.summary ? '&summary=1' : ''}`, token),
      getLabelList: (token, publicId, list, opts = {}) => apiGet(`/api/djpal/labels/${encodeURIComponent(publicId)}/${encodeURIComponent(list)}?page=${opts.page || 1}&limit=${opts.limit || 50}${opts.sort ? `&sort=${encodeURIComponent(opts.sort)}` : ''}`, token),
      getLabelArtistTracks: (token, labelPublicId, artistPublicId, opts = {}) => apiGet(`/api/djpal/labels/${encodeURIComponent(labelPublicId)}/artists/${encodeURIComponent(artistPublicId)}/tracks?kind=${encodeURIComponent(opts.kind || 'supporting')}&page=${opts.page || 1}&limit=${opts.limit || 20}`, token),
      autocompleteTrack: (token, q) => apiGet(`/api/djpal/tracks/autocomplete?q=${encodeURIComponent(q)}`, token),
      searchTracks: (token, q, page, limit) => apiGet(`/api/djpal/tracks/search?q=${encodeURIComponent(q)}&page=${page || 1}&limit=${limit || 50}`, token),
      searchTracksPlayedBy: (token, opts = {}) => {
        const params = new URLSearchParams({ page: String(opts.page || 1), limit: String(opts.limit || 50) });
        if (opts.artistPublicId) params.set('artist_id', opts.artistPublicId);
        if (!opts.artistPublicId && opts.artistName) params.set('artist', opts.artistName);
        if (opts.dateRange?.from) params.set('dateFrom', opts.dateRange.from);
        if (opts.dateRange?.to) params.set('dateTo', opts.dateRange.to);
        return apiGet(`/api/djpal/tracks/played-by?${params.toString()}`, token);
      },
      searchCoPlayedTracks: (token, opts = {}) => {
        const params = new URLSearchParams({ page: String(opts.page || 1), limit: String(opts.limit || 50) });
        if (opts.djArtistPublicId) params.set('dj_artist_id', opts.djArtistPublicId);
        else if (opts.djArtistName) params.set('dj_artist', opts.djArtistName);
        if (opts.trackArtistPublicId) params.set('track_artist_id', opts.trackArtistPublicId);
        else if (opts.trackArtistName) params.set('track_artist', opts.trackArtistName);
        if (opts.dateRange?.from) params.set('dateFrom', opts.dateRange.from);
        if (opts.dateRange?.to) params.set('dateTo', opts.dateRange.to);
        return apiGet(`/api/djpal/tracks/co-played?${params.toString()}`, token);
      },
      artistTrackPlays: (token, opts = {}) => {
        const params = new URLSearchParams({ by: opts.by || 'self', sort: opts.sort || 'recent', page: String(opts.page || 1), limit: String(opts.limit || 50) });
        if (opts.dateRange?.from) params.set('dateFrom', opts.dateRange.from);
        if (opts.dateRange?.to) params.set('dateTo', opts.dateRange.to);
        return apiGet(`/api/djpal/artists/${encodeURIComponent(opts.artistPublicId || '')}/track-plays?${params.toString()}`, token);
      },
      artistSupported: (token, opts = {}) => {
        const params = new URLSearchParams({ direction: opts.direction || 'by', sort: opts.sort || 'popularity', page: String(opts.page || 1), limit: String(opts.limit || 50) });
        if (opts.q) params.set('q', opts.q);
        return apiGet(`/api/djpal/artists/${encodeURIComponent(opts.artistPublicId || '')}/supported?${params.toString()}`, token);
      },
      autocompleteUnified: (token, q) => apiGet(`/api/djpal/search/autocomplete?q=${encodeURIComponent(q)}`, token),
      searchTrackRecommendations: (token, trackName, algorithm, variant, dateRange) =>
        apiPost('/api/djpal/tracks/recommendations/search', { trackName, algorithm, variant, dateRange }, token),
      playlistRecommendations: (token, source, algorithm, variant) =>
        apiPost('/api/djpal/tracks/recommendations/playlist', { ...(source || {}), algorithm, variant }, token),
      // Set Planner step. No main process here, so no server-backed step quota:
      // the renderer applies the free-plan rule locally (plannerLimits) when the
      // response carries no `quota` field.
      plannerRecommendations: (token, track, _opts) => {
        const seed = typeof track === 'string' ? { trackName: track } : (track || {});
        return apiPost('/api/djpal/tracks/recommendations/search', { ...seed, algorithm: 'NODE2VEC', variant: 'A' }, token);
      },
      fetchFeed: (token, params) => apiPost('/api/djpal/feed', params, token),
      fetchFeedAiPicks: (token, feedId) => apiPost('/api/djpal/feed/aipicks', { feedId }, token),
      fetchTrending: (token, params) => apiPost('/api/djpal/feed/trending', params || {}, token),
      extractStreamUrl: async (url) => {
        // Web (app.mixgoat.com), iOS, and Android are samples-only by design.
        // Playable tracks must already have a sampleUrl/previewUrl on them;
        // the stream-service path is intentionally not exercised here.
        // Full-stream playback only exists on the Electron desktop app (where
        // window.electron.djpal.extractStreamUrl is the real IPC handler in
        // main.js, NOT this shim — the shim is only used on web/mobile).
        return { url, fallback: true };
      },
      downloadForPeaks: () => Promise.resolve(null),  // Waveforms use preview URLs, not stream service
      // Client config (lazy modes, nativeMixCover, feedWaveform). No auth: the
      // endpoint is registered before the authenticate middleware. Mirrors the
      // capacitor-shim so renderer config utils have one cross-platform call.
      getClientConfig: () => apiGet('/api/djpal/config/lazy-resolve'),
      onNav: () => () => {}, // no-op, returns cleanup function
    },

    // ── Tier — reconciled (Stripe || Apple || Google) ──
    // Mirrors capacitor-shim.js: server-side /api/tier/reconciled is the
    // source of truth because it ORs Stripe with Apple/Google IAP
    // entitlements. The local fallback only knows about
    // `user.subscription_type` (computed from Stripe subscriptions in
    // auth-service), so it MISSES IAP — and a user who paid via Apple/Google
    // would incorrectly see the free-tier banner on web. Hit the server.
    tier: {
      startTrial: (accessToken) => apiPost('/api/subscriptions/start-trial', {}, accessToken),
      getInfo: async (user) => {
        const trialInfo = getTrialInfo(user);
        const token = authHeaderToken(user?.accessToken);
        // /api/tier/reconciled is on subscription-service; same proxy path
        // as /api/subscriptions/* used elsewhere in this shim.
        const reconciled = await apiGet('/api/tier/reconciled', token);
        if (reconciled && !reconciled.error) return { ...reconciled, trialInfo };
        // Fallback (network error / pre-auth): trial-only check + Stripe
        // hint from auth-service. IAP is invisible here, accepted as the
        // offline cost.
        if (user && user.subscription_type === 'goat') {
          return { tier: 'goat', reason: 'subscription', effectiveTier: 'goat', trialInfo, ...trialInfo };
        }
        if (trialInfo.isTrialActive) {
          return { tier: 'goat', reason: 'trial', effectiveTier: 'goat', trialInfo, ...trialInfo };
        }
        return { tier: 'free', reason: 'free', effectiveTier: 'free', trialInfo, ...trialInfo };
      },
      canAccess: async (featureKey, user) => {
        const info = await window.electron.tier.getInfo(user);
        const isGoat = info.effectiveTier === 'goat';
        return { allowed: isGoat, tier: isGoat ? 'goat' : 'free', featureLabel: featureKey };
      },
      getTrialInfo: (user) => Promise.resolve(getTrialInfo(user)),
    },

    // ── Subscription ──
    subscription: {
      getCurrent: (token) => apiGet('/api/subscriptions/current', token),
      // No auth required; returns currency/symbol/monthly/annual based on
      // the caller's geo (cf-ipcountry). Used by the upgrade modal.
      getPricing: () => apiGet('/api/subscriptions/pricing'),
      changePlan: (token, plan) => apiPost('/api/subscriptions/change-plan', { plan }, token),
      cancel: (token) => apiPost('/api/subscriptions/cancel', {}, token),
      reactivate: (token) => apiPost('/api/subscriptions/reactivate', {}, token),
      createSetupIntent: (token) => apiPost('/api/subscriptions/setup-intent', { mode: 'update-card', allowRedirects: false }, token),
      createSubscription: (token, plan, paymentMethodId) =>
        apiPost('/api/subscriptions/create', { plan, paymentMethodId }, token),
      updatePaymentMethod: (token, paymentMethodId) =>
        apiPost('/api/subscriptions/update-payment-method', { paymentMethodId }, token),
    },

    openUrl: (url) => {
      if (typeof url === 'string' && url.startsWith('https://')) window.open(url, '_blank');
      return Promise.resolve({ success: true });
    },
    onPaymentSuccess: () => () => {}, // no-op on web — handled by Paddle.js callback

    // ── App version (About page) ──
    // Desktop reads it over IPC from package.json; on web/native the same
    // value is baked in at build time (REACT_APP_VERSION, set from
    // package.json in config-overrides.js — versions are kept aligned
    // across all four targets, so the bundle version IS the app version).
    version: {
      get: () => Promise.resolve(process.env.REACT_APP_VERSION || null),
    },

    // ── Analytics (posthog-js in prod via window.__mixgoatPh, see
    //    src/analyticsClient.js; console.debug always) ──
    analytics: {
      track: (event, properties) => {
        console.debug('[analytics]', event, properties);
        window.__mixgoatPh?.phTrack('web', event, properties);
        return Promise.resolve();
      },
      identify: (userId, properties) => {
        console.debug('[analytics:identify]', userId, properties);
        window.__mixgoatPh?.phIdentify('web', userId, properties);
        return Promise.resolve();
      },
      setPersonProperties: (properties) => {
        console.debug('[analytics:setPersonProperties]', properties);
        window.__mixgoatPh?.phSetPersonProperties(properties);
        return Promise.resolve();
      },
      reset: () => {
        console.debug('[analytics:reset]');
        window.__mixgoatPh?.phReset();
        return Promise.resolve();
      },
    },

    // ── Store (localStorage) ──
    store: {
      get(key) {
        try {
          const val = localStorage.getItem(`store:${key}`);
          return val ? JSON.parse(val) : undefined;
        } catch { return undefined; }
      },
      set(key, val) {
        localStorage.setItem(`store:${key}`, JSON.stringify(val));
      },
    },

    // ── Desktop-only stubs (no-ops so code doesn't crash) ──
    getAudioUrl: () => Promise.resolve(null),
    getAlbumArtUrl: () => Promise.resolve(null),
    appReady: () => {},
    shell: { showItemInFolder: () => Promise.resolve(), openPath: () => Promise.resolve() },
    dialog: { showOpenDialog: () => Promise.resolve({ canceled: true }), showSaveDialog: () => Promise.resolve({ canceled: true }) },
    saveBuffer: () => Promise.resolve(),
    clipboard: { writeImage: () => Promise.resolve({ success: false, error: 'Not supported on web' }) },
    getPathForFile: () => '',
    openFile: () => Promise.resolve(null),
    openDir: () => Promise.resolve(null),
    resetFilePath: () => Promise.resolve(),
    cancelFixing: () => Promise.resolve(),
    cancelLoudnessMatching: () => Promise.resolve(),
    cancelOrganizing: () => Promise.resolve(),
    cancelAnalyzeInfo: () => Promise.resolve(),
    clearAnalysisCache: () => Promise.resolve(),
    cancelFixMetadata: () => Promise.resolve(),
    fixAllFilesInDir: () => {},
    matchAllFilesInDir: () => {},
    analyzeLibrary: () => {},
    organizeTracks: () => {},
    appendFileNames: () => {},
    analyzeMetadata: () => {},
    writeMetadata: () => {},

    // db is intentionally NOT defined — used as platform detection: if (!window.electron.db) → web
  };
})();
